import * as Sentry from "@sentry/react-native";
import config from "../config";

/**
 * Log levels in order of severity
 */
enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  NONE = 4
}

/**
 * Get the current log level from configuration
 */
function getCurrentLogLevel(): LogLevel {
  switch (config.logging.level) {
    case "DEBUG":
      return LogLevel.DEBUG;
    case "INFO":
      return LogLevel.INFO;
    case "WARN":
      return LogLevel.WARN;
    case "ERROR":
      return LogLevel.ERROR;
    case "NONE":
      return LogLevel.NONE;
    default:
      return LogLevel.INFO;
  }
}

const currentLogLevel = getCurrentLogLevel();

/**
 * Environment-aware logger utility that respects configured log levels
 */
const logger = {
  /**
   * Standard log method (required by some libraries)
   */
  log: (...args: any[]) => {
    if (currentLogLevel <= LogLevel.INFO) {
      console.log(...args);
    }
  },

  /**
   * Debug level logging
   */
  debug: (...args: any[]) => {
    if (currentLogLevel <= LogLevel.DEBUG) {
      console.debug(...args);
    }
  },

  /**
   * Info level logging
   */
  info: (...args: any[]) => {
    if (currentLogLevel <= LogLevel.INFO) {
      console.info(...args);
    }
  },

  /**
   * Warning level logging
   */
  warn: (...args: any[]) => {
    if (currentLogLevel <= LogLevel.WARN) {
      console.warn(...args);
    }
  },

  /**
   * Error level logging — also forwarded to Sentry when a DSN is configured.
   * The first `Error` in the args is captured as the exception; otherwise the
   * stringified args are captured as a message at level=error. Gated on the
   * DSN being set so dev / tests stay free of Sentry wiring.
   */
  error: (...args: any[]) => {
    if (currentLogLevel <= LogLevel.ERROR) {
      console.error(...args);
    }
    if (!config.sentry.dsn) return;
    const err = args.find((a) => a instanceof Error) as Error | undefined;
    if (err) {
      Sentry.captureException(err, { extra: { args } });
    } else {
      Sentry.captureMessage(args.map((a) => String(a)).join(" "), "error");
    }
  }
};

// Export the logger object
export { logger };

// Export default for easy importing
export default logger;
